<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">
                Admin: Projects: Environments
            </h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs -->
                <div class="mb-6">
                    <project-header-tabs
                        current="environments"
                        :project="project?.name"
                        :project-key="$route.params.projectKey"
                    />
                </div>

                <!-- Page Title and Action -->
                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">
                        Environments
                    </h2>
                    <div class="flex-shrink-0 flex items-center gap-3">
                        <zen-button
                            v-if="
                                !loading &&
                                swapsEnabled &&
                                hasResults &&
                                can(PERMISSIONS.ENVIRONMENT_UPDATE, {
                                    projectUlid: project?.ulid,
                                })
                            "
                            @click="swap.open()"
                            variant="secondary"
                        >
                            Create Swap
                        </zen-button>
                        <zen-button
                            v-if="!loading && swapsEnabled && hasResults"
                            @click="openSwapHistory"
                            variant="secondary"
                            data-testid="swap-history-trigger"
                        >
                            Swap history
                        </zen-button>
                        <zen-button
                            v-if="
                                !loading &&
                                can(PERMISSIONS.ENVIRONMENT_CREATE, {
                                    projectUlid: project?.ulid,
                                })
                            "
                            @click="openCreateModal()"
                            variant="primary"
                        >
                            <template #icon>
                                <svg
                                    class="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                                    />
                                </svg>
                            </template>
                            Create Environment
                        </zen-button>
                    </div>
                </div>

                <active-swaps-banner
                    v-if="
                        swapsEnabled &&
                        !loading &&
                        swap.active.overlays.length > 0
                    "
                    :swaps="swap.active.overlays"
                    :reverting="swap.active.revertingUlids"
                    :environments="environments"
                    @revert="revertSwap"
                />

                <zen-loading-indicator v-if="loading" />

                <!-- Environments Table -->
                <zen-grid v-if="!loading && hasResults">
                    <zen-grid-table>
                        <zen-grid-header>
                            <zen-grid-header-row>
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title="Name"
                                />
                                <zen-grid-header-cell title="Key" />
                                <zen-grid-header-cell
                                    :title="environmentKeyColumnTitle"
                                />
                                <zen-grid-header-cell
                                    alignment="right"
                                    title="Actions"
                                />
                            </zen-grid-header-row>
                        </zen-grid-header>
                        <zen-grid-body>
                            <zen-grid-body-row
                                v-for="result in results"
                                :key="result.ulid"
                            >
                                <zen-grid-body-cell
                                    :hidable="false"
                                    :primary="true"
                                >
                                    <router-link
                                        :to="{
                                            name: 'AdminProjectsSingleEnvironmentsWebhooksScreen',
                                            params: {
                                                projectKey:
                                                    $route.params.projectKey,
                                                environmentKey: result.key,
                                            },
                                        }"
                                        class="font-medium text-sky-600 hover:text-sky-500"
                                    >
                                        <div
                                            class="h-3 w-3 mr-1 rounded-sm inline-block"
                                            :style="
                                                makeColorBlock(result.color)
                                            "
                                        ></div>
                                        {{ result.name }}
                                    </router-link>
                                    <div
                                        class="lg:hidden text-sm text-taupe-500 font-mono"
                                    >
                                        {{ result.key }}
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <span
                                        class="font-mono text-sm text-taupe-900"
                                        >{{ result.key }}</span
                                    >
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <div
                                        class="rounded-md border border-taupe-200 bg-white"
                                        :data-testid="`admin-environment-keys-list-${result.ulid}`"
                                    >
                                        <div
                                            v-for="entry in getEnvironmentKeys(
                                                result
                                            )"
                                            :key="entry.id"
                                            class="border-b border-taupe-100 px-2.5 py-2 last:border-b-0"
                                            :data-testid="`admin-environment-key-row-${result.ulid}-${entry.id}`"
                                        >
                                            <div
                                                class="flex items-center gap-2"
                                            >
                                                <div
                                                    class="shrink-0 text-[11px] font-medium uppercase tracking-[0.08em] text-taupe-500"
                                                >
                                                    {{ entry.label }}
                                                </div>

                                                <zen-tooltip
                                                    v-if="entry.available"
                                                    content="Click to copy"
                                                    placement="top"
                                                    v-slot="{
                                                        setReference,
                                                        ariaAttrs,
                                                        show,
                                                        hide,
                                                    }"
                                                >
                                                    <button
                                                        type="button"
                                                        :ref="setReference"
                                                        v-bind="ariaAttrs"
                                                        :data-testid="`admin-environment-key-copy-${result.ulid}-${entry.id}`"
                                                        class="grow min-w-0 w-full cursor-pointer rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-left font-mono text-sm text-sky-800 whitespace-nowrap overflow-x-auto hover:bg-sky-100 hover:text-sky-900"
                                                        @mouseenter="show"
                                                        @mouseleave="hide"
                                                        @focus="show"
                                                        @blur="hide"
                                                        v-clipboard:copy="
                                                            entry.value
                                                        "
                                                        v-clipboard:success="
                                                            environmentKeyCopySuccessHandler(
                                                                entry.label
                                                            )
                                                        "
                                                        v-clipboard:error="
                                                            environmentKeyCopyErrorHandler(
                                                                entry.label
                                                            )
                                                        "
                                                    >
                                                        {{ entry.value }}
                                                    </button>
                                                </zen-tooltip>
                                                <div
                                                    v-else
                                                    class="ml-auto rounded-md border border-dashed border-taupe-200 bg-taupe-50 px-2 py-1 text-sm italic text-taupe-400"
                                                    :data-testid="`admin-environment-key-unavailable-${result.ulid}-${entry.id}`"
                                                >
                                                    Unavailable
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell alignment="right">
                                    <div class="flex justify-end gap-2">
                                        <zen-table-icon
                                            v-if="
                                                can(
                                                    PERMISSIONS.ENVIRONMENT_DELETE,
                                                    {
                                                        projectUlid:
                                                            project?.ulid,
                                                        environmentUlid:
                                                            result.ulid,
                                                    }
                                                )
                                            "
                                            @click="openRemoveModal(result.key)"
                                            :srText="'Remove ' + result.name"
                                            tooltipText="Remove"
                                        >
                                            <zen-icon-remove />
                                        </zen-table-icon>
                                    </div>
                                </zen-grid-body-cell>
                            </zen-grid-body-row>
                        </zen-grid-body>
                    </zen-grid-table>
                </zen-grid>

                <zen-empty-table-message
                    v-if="!loading && !hasResults"
                    title="No environments yet"
                    icon="ZenIconServer"
                >
                    There are no environments.
                </zen-empty-table-message>
            </div>
        </section>

        <create-environment-slide-in
            :is-open="modals.create.show"
            v-model:data="modals.create.data"
            :processing="modals.create.processing"
            :environment-name-to-slug="environmentNameToSlug"
            :check-environment-slug="checkEnvironmentSlug"
            :disallow-special-characters="disallowSpecialCharacters"
            @close="closeCreateModal()"
            @confirm="confirmCreate()"
        />

        <create-swap-slide-in
            v-if="swapsEnabled"
            :is-open="swap.modal.show"
            :environments="results ?? []"
            :initial-target-key="swap.modal.initialTargetKey"
            :diff="swap.modal.diff"
            :diff-loading="swap.modal.diffLoading"
            :processing="swap.modal.processing"
            @close="swap.close()"
            @preview="previewSwap"
            @confirm="confirmSwap"
        />

        <swap-history-slide-in
            v-if="swapsEnabled"
            :is-open="swap.history.show"
            :environments="results ?? []"
            :target-key="swap.history.targetKey"
            :events="swap.history.events"
            :actors="swap.history.actors"
            :filters="swap.history.filters"
            :loading="swap.history.loading"
            :loading-more="swap.history.loadingMore"
            :has-more="swap.history.hasMore"
            @close="swap.closeHistory()"
            @environment-change="selectHistoryEnvironment"
            @filter-change="applyHistoryFilters"
            @load-more="loadMoreHistory"
        />

        <remove-environment-modal
            :is-open="modals.remove.show"
            :processing="modals.remove.processing"
            @close="closeRemoveModal"
            @confirm="confirmRemove()"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import ProjectHeaderTabs from '@/screens/_components/admin/ProjectHeaderTabs.vue'
import ActiveSwapsBanner from '@/screens/_components/admin/ActiveSwapsBanner.vue'
import CreateEnvironmentSlideIn from '@/screens/trusted/admin/modals/CreateEnvironmentSlideIn.vue'
import CreateSwapSlideIn from '@/screens/trusted/admin/modals/CreateSwapSlideIn.vue'
import SwapHistorySlideIn from '@/screens/trusted/admin/modals/SwapHistorySlideIn.vue'
import RemoveEnvironmentModal from '@/screens/trusted/admin/modals/RemoveEnvironmentModal.vue'
import ProjectEnvironmentService from '@/services/project-environment.service'
import disallowSpecialCharactersImport from '@/helpers/disallow-special-characters'
import stringToKey from '@/helpers/string-to-key'
import {
    getEnvironmentKeyCopyErrorMessage,
    getEnvironmentKeyCopySuccessMessage,
    getMultiEnvironmentKeyEntries,
} from '@/utils/environment-keys'
import { errorMessage, infoMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'
import { useFeatureFlag } from '@/utils/feature-flags'
import { useSwapFlow } from '@/composables/useSwapFlow'
import { useSwapEndedEvents } from '@/composables/useSwapEndedEvents'

// Router and Store
const route = useRoute()
const sessionStore = useSessionStore()
const { can } = usePermissions()

// Temporary swaps ship dark: the entry point stays hidden until the flag is on.
const { enabled: swapsEnabled } = useFeatureFlag(
    'enable-temporary-swaps',
    false
)

// State
const loading = ref(false)
const results = ref(null)

const swap = useSwapFlow()

const modals = reactive({
    create: {
        environmentKeyNotChanged: true,
        show: false,
        processing: false,
        data: {
            name: null,
            key: null,
            description: null,
            color: null,
            comments_required: false,
            confirmation_required: false,
            can_overlay: true,
        },
    },
    remove: {
        show: false,
        processing: false,
        key: null,
    },
})

// Computed
const project = computed(() => {
    return sessionStore.data?.projects?.data?.filter(
        (project) => project.key === route.params.projectKey
    )[0]
})

const hasResults = computed(() => {
    return results.value != null && results.value.length > 0
})

// Swaps reference environments by ULID, so the banner looks names up from this list.
const environments = computed(() => results.value ?? [])

const environmentKeyColumnTitle = computed(() => 'Environment Keys')

// Methods
const makeColorBlock = (color) => {
    return 'background-color: ' + color
}

const onEnvironmentKeyCopy = (label) => {
    infoMessage(getEnvironmentKeyCopySuccessMessage(label))
}

const onEnvironmentKeyCopyError = (label) => {
    errorMessage(getEnvironmentKeyCopyErrorMessage(label))
}

const environmentKeyCopySuccessHandler = (label) => {
    return () => onEnvironmentKeyCopy(label)
}

const environmentKeyCopyErrorHandler = (label) => {
    return () => onEnvironmentKeyCopyError(label)
}

const getEnvironmentKeys = (environment) => {
    return getMultiEnvironmentKeyEntries(environment)
}

const disallowSpecialCharacters = (event) => {
    if (!disallowSpecialCharactersImport(event)) {
        event.preventDefault()
    }
}

const environmentNameToSlug = () => {
    if (modals.create.environmentKeyNotChanged) {
        modals.create.data.key = stringToKey(modals.create.data.name)
    }
}

const checkEnvironmentSlug = () => {
    modals.create.environmentKeyNotChanged =
        modals.create.data.key === stringToKey(modals.create.data.name)
}

const refresh = async () => {
    loading.value = true
    try {
        const data = await ProjectEnvironmentService.list(
            route.params.projectKey
        )
        results.value = data.data
        // Not awaited: the banner loads on its own, so a slow swap list never holds up the environments table.
        void loadActiveSwaps()
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

// Swaps are listed per target environment, so each environment on the screen is asked for its own.
const loadActiveSwaps = async () => {
    if (!swapsEnabled.value || !hasResults.value) {
        return
    }

    await swap.loadActive(
        route.params.projectKey,
        results.value.map((env) => env.key)
    )
}

const revertSwap = async (overlay) => {
    const target = environments.value.find(
        (env) => env.ulid === overlay.target_environment_ulid
    )
    if (!target) {
        return
    }

    await swap.revert(route.params.projectKey, target.key, overlay)
    // Reload from the API so the banner reflects what is still applying, not just what this screen removed.
    await loadActiveSwaps()
}

const openCreateModal = () => {
    modals.create.show = true
}

const closeCreateModal = () => {
    modals.create.show = false
    modals.create.data.name = null
    modals.create.data.key = null
    modals.create.data.description = null
    modals.create.data.color = null
    modals.create.data.comments_required = false
    modals.create.data.confirmation_required = false
    modals.create.data.can_overlay = true
    modals.create.environmentKeyNotChanged = true
}

const confirmCreate = async () => {
    modals.create.processing = true
    try {
        const data = await ProjectEnvironmentService.create(
            route.params.projectKey,
            modals.create.data
        )
        sessionStore.upsertEnvironment(route.params.projectKey, data.data)
        results.value = upsertBy(results.value, data.data, 'key')
        closeCreateModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.create.processing = false
    }
}

// Opens the history on the first environment, since the history is always for one target.
const openSwapHistory = () => {
    swap.openHistory(results.value?.[0]?.key ?? null)
    return swap.loadHistory(route.params.projectKey)
}

const selectHistoryEnvironment = (targetKey) =>
    swap.selectHistoryEnvironment(route.params.projectKey, targetKey)

const applyHistoryFilters = (filters) => {
    Object.assign(swap.history.filters, filters)
    return swap.loadHistory(route.params.projectKey)
}

const loadMoreHistory = () =>
    swap.loadHistory(route.params.projectKey, { append: true })

const previewSwap = (request) => swap.preview(route.params.projectKey, request)

const confirmSwap = async (request) => {
    await swap.confirm(route.params.projectKey, request)
    await loadActiveSwaps()
}

const openRemoveModal = (key) => {
    if (results.value.length <= 1) {
        errorMessage(
            'Sorry! The last environment cannot be removed from a project.'
        )
        return
    }

    modals.remove.key = key
    modals.remove.show = true
}

const closeRemoveModal = () => {
    modals.remove.show = false
    modals.remove.key = null
}

const confirmRemove = async () => {
    modals.remove.processing = true
    try {
        await ProjectEnvironmentService.remove(
            route.params.projectKey,
            modals.remove.key
        )
        sessionStore.removeEnvironment(
            route.params.projectKey,
            modals.remove.key
        )
        results.value = removeBy(results.value, modals.remove.key, 'key')
        closeRemoveModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.remove.processing = false
    }
}

// A swap ended, by someone reverting it or by running out. Reload what still applies and, when the history is
// open, its newest rows, so the screen matches the server without a reload (ZEN-2030).
useSwapEndedEvents((event) => {
    if (event && event.projectKey !== route.params.projectKey) {
        return
    }

    void loadActiveSwaps()
    if (swap.history.show) {
        void swap.loadHistory(route.params.projectKey)
    }
})

// Lifecycle
onMounted(() => {
    refresh()
})
</script>
